import * as Sentry from "@sentry/browser";
import React, { Fragment, useEffect, useState } from "react";

import { Column, Container, Icon, Row, useToast } from "../components";
import { useOfferingDailyAccessQuantityFieldFeatureFlag } from "../core/splitio";
import { Location, PricingType, Space } from "../data";
import { PartnerOfferingLocation } from "../data/partner_offering";
import { formatCurrency } from "../lib/currency/currency";
import { FirstDayOfWeek } from "../lib/datetime";
import { useI18n } from "../lib/i18n/i18n_provider";
import { getSystemLocale } from "../lib/locale/locale";
import { AvailableTimeSlot, SpaceOffering, Quote } from "./api";
import { Button } from "./button";
import { CapacityPicker } from "./capacity_picker";
import {
  CheckoutInfoMessage,
  QuantityNotAvailableErrorMessage,
} from "./checkout_box_mobile";
import { DateRangePicker } from "./date_range_picker";
import { Divider } from "./divider";
import { LocationSpacePicker } from "./location_space_picker";
import { MeetingRoomDateRangePicker } from "./meeting_room_date_range_picker";
import { MultiPassPicker } from "./multipass_picker";
import {
  DailyAccessOrderParams,
  DailyAccessOrderParamsErrors,
  DayOfficeOrderParams,
  DayOfficeOrderParamsErrors,
  DayPassDateRange,
  DayPassOrderParams,
  DayPassWithCapacityOrderParams,
  DayPassWithCapacityOrderParamsErrors,
  MeetingRoomDateRange,
  MeetingRoomOrderParams,
  MeetingRoomOrderParamsErrors,
  MultiDayOfficeOrderParams,
  MultiDayOfficeOrderParamsErrors,
  MultiPassOrderParams,
  MultiPassOrderParamsErrors,
  OrderParams,
} from "./order_params";
import { PartnerOfferingLocationPicker } from "./partner_location_picker";
import { QuantityPicker } from "./quantity_picker";
import { QuoteView } from "./quote_view";
import { Spacer } from "./spacer";
import { Text } from "./text";
import { useAnalytics } from "../lib/analytics/analytics";
import { useIsWework } from "./hooks/use_is_wework";
import { isHourlyValue } from "@/offering_v2/components/date-time-picker/date_time_picker";

export const pickerOverride = {
  view: {
    style: { paddingLeft: 8, paddingRight: 8, borderRadius: 7 },
  },
};

interface DayPassCheckoutBoxDesktopProps {
  quote: Quote | undefined;
  quoteError: string;
  quoteLoading: boolean;
  includeQuantity: boolean;
  locations: PartnerOfferingLocation[];
  space: Space | null | undefined;
  location: Location | null | undefined;
  orderParams: DayPassOrderParams;
  orderParamsErrors?: MeetingRoomOrderParamsErrors;
  onNextVisibleDates: (startDate: Date, endDate: Date) => void;
  onChangeLocation: (spaceID: string) => void;
  onChangeQuantity: (qty: number) => void;
  onChangeDateRange: (dateRange?: DayPassDateRange) => void;
  onContinue?: () => void;
  isOutsideRange: (date: Date) => boolean;
  isBlockedDate: (date: Date) => boolean;
  firstDayOfWeek?: FirstDayOfWeek;

  // UI overrides
  showCTA?: boolean;
  datePickerPlaceholder?: string;
}

export function DayPassCheckoutBoxDesktop(
  props: DayPassCheckoutBoxDesktopProps
) {
  const {
    includeQuantity,
    quoteLoading,
    quote,
    quoteError,
    location,
    orderParams,
    orderParamsErrors,
    locations,
    space,
    onChangeLocation,
    onChangeQuantity,
    onChangeDateRange,
    onContinue,
    isOutsideRange,
    isBlockedDate,
    onNextVisibleDates,
    firstDayOfWeek,
    showCTA,
    datePickerPlaceholder,
  } = props;
  const { fbt } = useI18n();
  let error;

  if (orderParamsErrors?.dateRange) {
    error = <Text color="error">{orderParamsErrors?.dateRange}</Text>;
  } else if (orderParamsErrors?.spaceID) {
    error = <Text color="error">{orderParamsErrors?.spaceID}</Text>;
  } else if (includeQuantity && quoteError) {
    error = <QuantityNotAvailableErrorMessage orderParams={orderParams} />;
  }

  const showPrice = !!(
    !orderParams.dateRange?.startDate || !orderParams.dateRange?.endDate
  );

  return (
    <CheckoutBoxDesktop
      quoteLoading={quoteLoading}
      includeQuantity={includeQuantity}
      quote={quote}
      location={location}
      error={error}
      space={space}
      orderParams={orderParams}
      onContinue={onContinue}
      showCTA={showCTA}
      pickers={[
        <LocationSpacePicker
          value={orderParams.spaceID}
          locations={locations}
          onChange={onChangeLocation}
          override={pickerOverride}
        />,
        <DateRangePicker
          firstDayOfWeek={firstDayOfWeek}
          view="dialog"
          ctaTitle={fbt("Save")}
          value={orderParams.dateRange}
          isBlockedDate={isBlockedDate}
          isOutsideRange={isOutsideRange}
          onNextVisibleDates={onNextVisibleDates}
          onComplete={(v) => onChangeDateRange(v)}
          override={pickerOverride}
          placeholder={datePickerPlaceholder}
        />,
        includeQuantity && onChangeQuantity && (
          <QuantityPicker
            value={orderParams.quantity}
            onChange={onChangeQuantity}
            override={pickerOverride}
          />
        ),
      ]}
      showPrice={showPrice}
    />
  );
}

interface DayPassWithCapacityCheckoutBoxDesktopProps {
  quote: Quote | undefined;
  quoteError: string;
  capacities: number[];
  capacityOptionLabel?: string;
  quoteLoading: boolean;
  locations: PartnerOfferingLocation[];
  space: Space | null | undefined;
  location: Location | null | undefined;
  orderParams: DayPassWithCapacityOrderParams;
  orderParamsErrors?: DayPassWithCapacityOrderParamsErrors;
  onNextVisibleDates: (startDate: Date, endDate: Date) => void;
  onChangeLocation: (spaceID: string) => void;
  onChangeCapacity: (qty: number) => void;
  onChangeDateRange: (dateRange?: DayPassDateRange) => void;
  onContinue?: () => void;
  isOutsideRange: (date: Date) => boolean;
  isBlockedDate: (date: Date) => boolean;
  firstDayOfWeek?: FirstDayOfWeek;

  // UI overrides
  showCTA?: boolean;
  datePickerPlaceholder?: string;
}

export function DayPassWithCapacityCheckoutBoxDesktop(
  props: DayPassWithCapacityCheckoutBoxDesktopProps
) {
  const {
    quoteLoading,
    quote,
    location,
    capacities,
    capacityOptionLabel,
    orderParams,
    orderParamsErrors,
    locations,
    space,
    onChangeLocation,
    onChangeCapacity,
    onChangeDateRange,
    onContinue,
    isOutsideRange,
    isBlockedDate,
    onNextVisibleDates,
    firstDayOfWeek,
    showCTA,
    datePickerPlaceholder,
  } = props;
  const { fbt } = useI18n();
  let error;

  if (orderParamsErrors?.dateRange) {
    error = <Text color="error">{orderParamsErrors?.dateRange}</Text>;
  } else if (orderParamsErrors?.spaceID) {
    error = <Text color="error">{orderParamsErrors?.spaceID}</Text>;
  } else if (orderParamsErrors?.capacity) {
    error = <Text color="error">{orderParamsErrors?.capacity}</Text>;
  } else if (
    space?.inventoryCapacity !== undefined &&
    space?.inventoryCapacity < 1
  ) {
    error = (
      <Text color="error">
        {fbt("There are no offices available for the selected capacity.")}
      </Text>
    );
  }

  const showPrice = !!(
    !orderParams.dateRange?.startDate || !orderParams.dateRange?.endDate
  );

  return (
    <CheckoutBoxDesktop
      quoteLoading={quoteLoading}
      quote={quote}
      location={location}
      error={error}
      space={space}
      orderParams={orderParams}
      onContinue={onContinue}
      showCTA={showCTA}
      pickers={[
        <PartnerOfferingLocationPicker
          value={orderParams.partnerOfferingLocationID}
          locations={locations}
          onChange={onChangeLocation}
          override={pickerOverride}
        />,
        <CapacityPicker
          capacities={capacities}
          value={orderParams.capacity}
          onChange={onChangeCapacity}
          override={pickerOverride}
          optionLabel={capacityOptionLabel}
        />,
        <DateRangePicker
          firstDayOfWeek={firstDayOfWeek}
          view="dialog"
          ctaTitle={fbt("Save")}
          value={orderParams.dateRange}
          isBlockedDate={isBlockedDate}
          isOutsideRange={isOutsideRange}
          onNextVisibleDates={onNextVisibleDates}
          onComplete={(v) => onChangeDateRange(v)}
          override={pickerOverride}
          placeholder={datePickerPlaceholder}
        />,
      ]}
      showPrice={showPrice}
    />
  );
}

interface DayOfficeCheckoutBoxDesktopProps {
  quote: Quote | undefined;
  quoteError: string;
  quoteLoading: boolean;
  space: Space | null | undefined;
  location: Location | null | undefined;
  orderParams: DayOfficeOrderParams;
  orderParamsErrors?: DayOfficeOrderParamsErrors;
  onNextVisibleDates: (startDate: Date, endDate: Date) => void;
  onChangeDateRange: (dateRange?: DayPassDateRange) => void;
  onContinue?: () => void;
  isOutsideRange: (date: Date) => boolean;
  isBlockedDate: (date: Date) => boolean;
  firstDayOfWeek?: FirstDayOfWeek;

  // UI overrides
  showCTA?: boolean;
  datePickerPlaceholder?: string;
}

export function DayOfficeCheckoutBoxDesktop(
  props: DayOfficeCheckoutBoxDesktopProps
) {
  const {
    quoteLoading,
    quote,
    location,
    orderParams,
    orderParamsErrors,
    space,
    onChangeDateRange,
    onContinue,
    isOutsideRange,
    isBlockedDate,
    onNextVisibleDates,
    firstDayOfWeek,
    showCTA,
    datePickerPlaceholder,
  } = props;
  const { fbt } = useI18n();
  let error;

  if (orderParamsErrors?.dateRange) {
    error = <Text color="error">{orderParamsErrors?.dateRange}</Text>;
  } else if (orderParamsErrors?.spaceID) {
    error = <Text color="error">{orderParamsErrors?.dateRange}</Text>;
  }

  const showPrice = !!(
    !orderParams.dateRange?.startDate || !orderParams.dateRange?.endDate
  );

  return (
    <CheckoutBoxDesktop
      quoteLoading={quoteLoading}
      quote={quote}
      location={location}
      error={error}
      space={space}
      onContinue={onContinue}
      orderParams={orderParams}
      showCTA={showCTA}
      pickers={[
        <DateRangePicker
          firstDayOfWeek={firstDayOfWeek}
          view="dialog"
          ctaTitle={fbt("Save")}
          value={orderParams.dateRange}
          isBlockedDate={isBlockedDate}
          isOutsideRange={isOutsideRange}
          onNextVisibleDates={onNextVisibleDates}
          onComplete={(v) => onChangeDateRange(v)}
          override={pickerOverride}
          placeholder={datePickerPlaceholder}
        />,
      ]}
      showPrice={showPrice}
    />
  );
}

interface MeetingRoomCheckoutBoxDesktopProps {
  quote?: Quote;
  quoteLoading: boolean;
  space: Space | null | undefined;
  location: Location | null | undefined;
  orderParams: MeetingRoomOrderParams;
  orderParamsErrors?: MeetingRoomOrderParamsErrors;
  onNextVisibleDates: (startDate: Date, endDate: Date) => void;
  onChangeDateRange: (dateRange?: MeetingRoomDateRange) => void;
  onContinue?: () => void;
  onSelectDate: (date: Date) => void;
  isOutsideRange: (date: Date) => boolean;
  isBlockedDate: (date: Date) => boolean;
  timeslots: AvailableTimeSlot[];
  firstDayOfWeek?: FirstDayOfWeek;
  availableDaysError?: string;
  availableTimeSlotsError?: string;
  // UI overrides
  showCTA?: boolean;
  datePickerPlaceholder?: string;
}

export function MeetingRoomCheckoutBoxDesktop(
  props: MeetingRoomCheckoutBoxDesktopProps
) {
  const {
    quoteLoading,
    quote,
    location,
    orderParams,
    orderParamsErrors,
    space,
    timeslots,
    firstDayOfWeek,
    onChangeDateRange,
    onContinue,
    isOutsideRange,
    isBlockedDate,
    onNextVisibleDates,
    onSelectDate,
    availableDaysError,
    availableTimeSlotsError,
    showCTA,
    datePickerPlaceholder,
  } = props;
  const { fbt } = useI18n();
  const showPrice =
    isHourlyValue(orderParams.dateRange) &&
    !!(
      !orderParams.dateRange?.date ||
      !orderParams.dateRange?.startTime ||
      !orderParams.dateRange?.endTime
    );
  const [hasAvailabilityError, setHasAvailabilityError] = useState(false);
  const [hasShownAvailabilityError, setHasShownAvailabilityError] =
    useState(false);
  const toast = useToast();

  useEffect(() => {
    const hasError = !!availableDaysError || !!availableTimeSlotsError;
    setHasAvailabilityError(hasError);
    if (!hasError) {
      setHasShownAvailabilityError(false);
    } else if (!hasShownAvailabilityError) {
      toast.error({ message: fbt("availabilityFetchingError") });
      Sentry.withScope((scope) => {
        scope.setExtras({ space_id: space?.id });
        Sentry.captureMessage(
          `Unable to check availability ${
            availableDaysError || availableTimeSlotsError
          }`
        );
      });
      setHasShownAvailabilityError(true);
    }
  }, [
    availableDaysError,
    availableTimeSlotsError,
    hasShownAvailabilityError,
    toast,
    fbt,
    space,
  ]);

  return (
    <CheckoutBoxDesktop
      quote={quote}
      quoteLoading={quoteLoading}
      location={location}
      error={orderParamsErrors?.dateRange || orderParamsErrors?.spaceID}
      space={space}
      onContinue={onContinue}
      orderParams={orderParams}
      showCTA={showCTA}
      pickers={[
        <MeetingRoomDateRangePicker
          firstDayOfWeek={firstDayOfWeek}
          view="dialog"
          ctaTitle={fbt("Save")}
          timeslots={timeslots}
          value={
            isHourlyValue(orderParams.dateRange)
              ? orderParams.dateRange
              : undefined
          }
          onComplete={onChangeDateRange}
          onNextVisibleDates={onNextVisibleDates}
          onSelectDate={onSelectDate}
          isBlockedDate={isBlockedDate}
          isOutsideRange={isOutsideRange}
          override={pickerOverride}
          placeholder={datePickerPlaceholder}
          disabled={hasAvailabilityError}
        />,
      ]}
      showPrice={showPrice}
    />
  );
}

interface LocationPreviewProps {
  location: Location;
}

export function LocationPreview(props: LocationPreviewProps) {
  const { location } = props;
  return (
    <Container paddingHorizontal={16} height={64}>
      <Row expanded alignItems="center" flex={1}>
        <Icon name="map-pin" />
        <Spacer size={8} direction="horizontal" />
        <Column flex={1}>
          <Text>{location?.city}</Text>
          <Text size="sm">{location?.streetAddress}</Text>
        </Column>
      </Row>
    </Container>
  );
}

interface MultiCheckoutBoxDesktopProps {
  quote?: Quote;
  quoteLoading: boolean;
  locations: PartnerOfferingLocation[];
  space: Space | null | undefined;
  location: Location | null | undefined;
  onChangeLocation: (spaceID: string) => void;
  multiPasses: SpaceOffering[];
  onChangeMultiPass: (addOnID: string, spaceOfferingID: string) => void;
  onContinue?: () => void;
  showCTA?: boolean;
}

interface MultiPassCheckoutBoxDesktopProps
  extends MultiCheckoutBoxDesktopProps {
  orderParams: MultiPassOrderParams;
  orderParamsErrors?: MultiPassOrderParamsErrors;
}

export function MultiPassCheckoutBoxDesktop(
  props: MultiPassCheckoutBoxDesktopProps
) {
  const {
    quote,
    quoteLoading,
    location,
    orderParams,
    orderParamsErrors,
    locations,
    multiPasses,
    space,
    onChangeLocation,
    onChangeMultiPass,
    onContinue,
    showCTA,
  } = props;

  return (
    <CheckoutBoxDesktop
      quote={quote}
      quoteLoading={quoteLoading}
      location={location}
      error={orderParamsErrors?.addOnID || orderParamsErrors?.spaceID}
      space={space}
      onContinue={onContinue}
      showCTA={showCTA}
      showPrice={false}
      orderParams={orderParams}
      pickers={[
        <LocationSpacePicker
          value={orderParams.spaceID}
          locations={locations}
          onChange={onChangeLocation}
          override={pickerOverride}
        />,
        <MultiPassPicker
          multiPasses={multiPasses}
          value={orderParams.addOnID}
          onChange={onChangeMultiPass}
          override={pickerOverride}
        />,
      ]}
    />
  );
}

interface MultiDayOfficeCheckoutBoxDesktopProps
  extends MultiCheckoutBoxDesktopProps {
  orderParams: MultiDayOfficeOrderParams;
  orderParamsErrors?: MultiDayOfficeOrderParamsErrors;
  capacities: number[];
  capacityOptionLabel?: string;
  onChangeCapacity: (qty: number) => void;
}

export function MultiDayOfficeCheckoutBoxDesktop(
  props: MultiDayOfficeCheckoutBoxDesktopProps
) {
  const {
    quote,
    quoteLoading,
    location,
    orderParams,
    orderParamsErrors,
    locations,
    multiPasses,
    space,
    capacities,
    capacityOptionLabel,
    onChangeCapacity,
    onChangeLocation,
    onChangeMultiPass,
    onContinue,
    showCTA,
  } = props;

  return (
    <CheckoutBoxDesktop
      quote={quote}
      quoteLoading={quoteLoading}
      location={location}
      error={
        orderParamsErrors?.addOnID ||
        orderParamsErrors?.partnerOfferingLocationID
      }
      space={space}
      onContinue={onContinue}
      showCTA={showCTA}
      showPrice={false}
      orderParams={orderParams}
      pickers={[
        <PartnerOfferingLocationPicker
          value={orderParams.partnerOfferingLocationID}
          locations={locations}
          onChange={onChangeLocation}
          override={pickerOverride}
        />,
        <CapacityPicker
          capacities={capacities}
          value={orderParams.capacity}
          onChange={onChangeCapacity}
          override={pickerOverride}
          optionLabel={capacityOptionLabel}
        />,
        <MultiPassPicker
          multiPasses={multiPasses}
          value={orderParams.addOnID}
          onChange={onChangeMultiPass}
          override={pickerOverride}
        />,
      ]}
    />
  );
}

interface DailyAccessCheckoutBoxDesktopProps {
  quote: Quote | undefined;
  quoteError: string;
  quoteLoading: boolean;
  includeQuantity: boolean;
  limitQuantityPicker?: number;
  locations: PartnerOfferingLocation[];
  space: Space | null | undefined;
  location: Location | null | undefined;
  orderParams: DailyAccessOrderParams;
  orderParamsErrors?: DailyAccessOrderParamsErrors;
  onNextVisibleDates: (startDate: Date, endDate: Date) => void;
  onChangeLocation: (spaceID: string) => void;
  onChangeDateRange: (dateRange?: DayPassDateRange) => void;
  onChangeQuantity: (qty: number) => void;
  multiPasses: SpaceOffering[];
  onChangeAddOnID: (addOnID: string, spaceOfferingID: string) => void;
  onContinue?: () => void;
  isOutsideRange: (date: Date) => boolean;
  isBlockedDate: (date: Date) => boolean;
  firstDayOfWeek?: FirstDayOfWeek;

  // UI overrides
  showCTA?: boolean;
  datePickerPlaceholder?: string;
}

export function DailyAccessCheckoutBoxDesktop(
  props: DailyAccessCheckoutBoxDesktopProps
) {
  const {
    includeQuantity,
    limitQuantityPicker,
    quoteError,
    quoteLoading,
    quote,
    multiPasses,
    location,
    orderParams,
    orderParamsErrors,
    locations,
    space,
    onChangeLocation,
    onChangeAddOnID,
    onChangeDateRange,
    onChangeQuantity,
    onContinue,
    isOutsideRange,
    isBlockedDate,
    onNextVisibleDates,
    firstDayOfWeek,
    showCTA,
    datePickerPlaceholder,
  } = props;
  const { fbt } = useI18n();
  let error;

  if (orderParamsErrors?.dateRange) {
    error = <Text color="error">{orderParamsErrors?.dateRange}</Text>;
  } else if (orderParamsErrors?.spaceID) {
    error = <Text color="error">{orderParamsErrors?.spaceID}</Text>;
  } else if (orderParamsErrors?.addOnID) {
    error = <Text color="error">{orderParamsErrors?.addOnID}</Text>;
  } else if (includeQuantity && quoteError) {
    error = <QuantityNotAvailableErrorMessage orderParams={orderParams} />;
  }

  const showPrice = !!(
    orderParams.addOnID === "daypass" &&
    (!orderParams.dateRange?.startDate || !orderParams.dateRange?.endDate)
  );

  const mp = multiPasses.find(
    (mp) =>
      mp.addOnID === orderParams.addOnID || orderParams.addOnID === "daypass"
  );
  const displayQuantityField = useOfferingDailyAccessQuantityFieldFeatureFlag();

  return (
    <CheckoutBoxDesktop
      includeQuantity={includeQuantity}
      quoteLoading={quoteLoading}
      quote={quote}
      location={location}
      error={error}
      space={space}
      orderParams={orderParams}
      onContinue={onContinue}
      showCTA={showCTA}
      pickers={[
        <LocationSpacePicker
          value={orderParams.spaceID}
          locations={locations}
          onChange={onChangeLocation}
          override={pickerOverride}
        />,
        <MultiPassPicker
          multiPasses={multiPasses}
          value={orderParams.addOnID}
          onChange={onChangeAddOnID}
          override={pickerOverride}
          icon="box"
        />,
        mp?.numberOfEntries && mp.numberOfEntries === 1 && (
          <DateRangePicker
            firstDayOfWeek={firstDayOfWeek}
            view="dialog"
            ctaTitle={fbt("Save")}
            placeholder={datePickerPlaceholder}
            value={orderParams.dateRange}
            isBlockedDate={isBlockedDate}
            isOutsideRange={isOutsideRange}
            onNextVisibleDates={onNextVisibleDates}
            onComplete={(v) => onChangeDateRange(v)}
            override={pickerOverride}
          />
        ),
        mp?.numberOfEntries &&
          mp.numberOfEntries === 1 &&
          includeQuantity &&
          onChangeQuantity &&
          displayQuantityField && (
            <QuantityPicker
              value={orderParams.quantity}
              onChange={onChangeQuantity}
              override={pickerOverride}
              limitQuantityPicker={limitQuantityPicker}
              label="Attendees:"
              icon="capacity"
            />
          ),
      ]}
      showPrice={showPrice}
    />
  );
}

interface CheckoutBoxDesktopProps {
  quote?: Quote;
  quoteLoading: boolean;
  includeQuantity?: boolean;
  space: Space | null | undefined;
  location: Location | null | undefined;
  orderParams: OrderParams;

  error?: React.ReactNode;
  onContinue?: () => void;
  showPrice: boolean;
  pickers: React.ReactNode[];

  // UI overrides
  showCTA?: boolean;
}

export function CheckoutBoxDesktop(props: CheckoutBoxDesktopProps) {
  const {
    quote,
    includeQuantity,
    location,
    error,
    space,
    orderParams,

    onContinue,
    showPrice,
    pickers,
    showCTA = true,
  } = props;
  const disabled = !quote;
  const { fbt } = useI18n();

  const isWeWork = useIsWework();

  return (
    <div className="checkout-box-desktop">
      {pickers.map((p, i) => {
        if (!p) {
          return null;
        }
        return (
          <Fragment key={i}>
            <div className="checkout-box-desktop-picker">{p}</div>
            <Divider />
          </Fragment>
        );
      })}
      {showPrice && space && space.pricings[0].type === PricingType.Daily && (
        <Container paddingTop={16}>
          <Text weight="bold" align="center" size="xl">
            {formatCurrency(
              space.pricings[0].price,
              space.currencyCode,
              getSystemLocale()
            )}
            <Text size="xl">
              {isWeWork ? ` ${fbt("before VAT per day")}` : fbt("/day")}
            </Text>
          </Text>
        </Container>
      )}
      {showPrice && space && space.pricings[0].type === PricingType.Hourly && (
        <Container paddingTop={16}>
          <Text weight="bold" align="center" size="xl">
            {formatCurrency(
              space.pricings[0].price,
              space.currencyCode,
              getSystemLocale()
            )}
            <Text size="xl">
              {isWeWork ? ` ${fbt("before VAT per hour")}` : fbt("/hr")}
            </Text>
          </Text>
        </Container>
      )}
      {quote && space && location && (
        <Container paddingHorizontal={16}>
          <QuoteView
            orderParams={orderParams}
            locationTimezone={location?.timezone}
            quote={quote}
            currency={space.currencyCode}
            pricingInclusion={location.pricingInclusion}
            includeQuantity={includeQuantity}
          />
        </Container>
      )}
      {error && (
        <Container paddingTop={16} paddingHorizontal={16}>
          {error}
        </Container>
      )}
      {showCTA && <CheckoutCTA onContinue={onContinue} disabled={disabled} />}
      <CheckoutInfoMessage quote={quote} orderParams={orderParams} />
    </div>
  );
}

interface CheckoutCTAProps {
  disabled: boolean;
  onContinue?: () => void;
}

function CheckoutCTA(props: CheckoutCTAProps) {
  const { disabled, onContinue } = props;
  const { fbt } = useI18n();
  const analytics = useAnalytics();

  return (
    <Container paddingHorizontal={16}>
      <Spacer size={16} />
      <Button
        onPress={() => {
          if (onContinue) onContinue();

          analytics.event(
            "Quote Box Continue Click",
            {
              Source: "WL",
            },
            { onlyProviders: ["mixpanel", "customerio"] }
          );
        }}
        disabled={disabled}
        size="lg"
        title={fbt("Continue")}
      />
      <Spacer size={16} />
    </Container>
  );
}
